import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Button, Switch } from '@vitness/ui';
import { useTheme, useAccent, useDensity } from '@vitness/theme';
import {
  Palette, Smartphone, RefreshCw, Database, UserRound, Scale, CalendarClock,
  CheckCircle2, AlertCircle, ExternalLink, Check,
} from '@vitness/icons';
import { useStore } from '../../lib/store';
import { api, type NotionWebhookStatus } from '../../lib/api';
import { NAV_ITEMS, DEFAULT_PINNED } from '../../shell/nav';
import { useUiScale, UI_SCALES } from '../../lib/useUiScale';
import { fmtMoney } from '../../lib/format';
import { BUILD, buildDate } from '../../lib/build';
import { PULL_TO_REFRESH_KEY } from '../../components/PullToRefresh';
import { FundingSchedulesPanel } from './FundingSchedulesPanel';

export const SETTINGS_SECTIONS = [
  { id: 'appearance', label: 'Appearance', icon: Palette },
  { id: 'navigation', label: 'Navigation', icon: Smartphone },
  { id: 'sync', label: 'Sync', icon: RefreshCw },
  { id: 'paychecks', label: 'Paychecks', icon: CalendarClock },
  { id: 'data', label: 'Data', icon: Database },
  { id: 'account', label: 'Account', icon: UserRound },
] as const;

export type SettingsSectionId = (typeof SETTINGS_SECTIONS)[number]['id'];

/**
 * Settings body — a rail of sections beside a single scrolling column of
 * labelled rows. Rendered as a page at /settings and inside a dialog from the
 * account popover, so it takes no layout decisions of its own.
 */
export function SettingsPanel({ section, onSection, compact = false }: {
  section: SettingsSectionId;
  onSection: (id: SettingsSectionId) => void;
  compact?: boolean;
}) {
  return (
    <div className={compact ? 'vd-settings vd-settings--compact' : 'vd-settings'}>
      <nav className="vd-settings__rail">
        {SETTINGS_SECTIONS.map(s => {
          const Icon = s.icon;
          const on = section === s.id;
          return (
            <button key={s.id} onClick={() => onSection(s.id)}
              className={`vd-settings__tab${on ? ' is-active' : ''}`}>
              <Icon size={14} />
              <span>{s.label}</span>
            </button>
          );
        })}
      </nav>

      <div className="vd-settings__body">
        {section === 'appearance' && <AppearanceSection />}
        {section === 'navigation' && <NavigationSection />}
        {section === 'sync' && <SyncSection />}
        {section === 'paychecks' && <PaychecksSection />}
        {section === 'data' && <DataSection />}
        {section === 'account' && <AccountSection />}
      </div>
    </div>
  );
}

/**
 * The paycheck cadences, and whether each covers what it funds.
 *
 * Its own section rather than a row under Sync: this is where the answer to
 * "is this line on 24 or 26?" lives, and that question is what silently made
 * every 26-cadence figure 7.69% short for as long as the app has existed.
 */
function PaychecksSection() {
  const data = useStore(s => s.data);
  return (
    <Group title="Paycheck cadences"
      description="Read-only, from Notion. The divisor behind every set-aside, and the calendar behind every projection.">
      <FundingSchedulesPanel
        schedules={data?.fundingSchedules ?? []}
        expenses={data?.expenses ?? []}
        income={data?.income ?? {
          netMonthly: 0, grossMonthly: 0, netGuaranteedMonthly: 0,
          grossGuaranteedMonthly: 0, netAnnual: 0, grossAnnual: 0, sources: [],
        }}
      />
    </Group>
  );
}

// ───────────────────────── building blocks ─────────────────────────

function Group({ title, description, children }: { title: string; description?: string; children: ReactNode }) {
  return (
    <section style={{ marginBottom: 28 }}>
      <h2 style={{
        fontFamily: 'var(--vn-font-display)', fontSize: 15, fontWeight: 700,
        margin: 0, letterSpacing: '-0.01em',
      }}>
        {title}
      </h2>
      {description && (
        <p style={{ margin: '4px 0 0', fontSize: 12.5, color: 'var(--vn-text-secondary)', lineHeight: 1.5 }}>
          {description}
        </p>
      )}
      <div style={{
        marginTop: 14, border: '1px solid var(--vn-border-default)',
        borderRadius: 'var(--vn-radius-lg)', overflow: 'hidden',
        background: 'var(--vn-surface-card)',
      }}>
        {children}
      </div>
    </section>
  );
}

/** One setting: label + help on the left, control on the right. */
function Row({ label, help, control, stacked }: {
  label: string; help?: string; control: ReactNode; stacked?: boolean;
}) {
  return (
    <div style={{
      display: 'flex', alignItems: stacked ? 'flex-start' : 'center',
      justifyContent: 'space-between', gap: 16, flexWrap: stacked ? 'wrap' : 'nowrap',
      padding: '13px 15px', borderBottom: '1px solid var(--vn-border-default)',
    }}>
      <div style={{ minWidth: 0, flex: '1 1 200px' }}>
        <div style={{ fontSize: 13, fontWeight: 600 }}>{label}</div>
        {help && (
          <div style={{ fontSize: 11.5, color: 'var(--vn-text-muted)', marginTop: 2, lineHeight: 1.5 }}>
            {help}
          </div>
        )}
      </div>
      <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        {control}
      </div>
    </div>
  );
}

function Fact({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', gap: 12,
      padding: '10px 15px', borderBottom: '1px solid var(--vn-border-default)',
      fontFamily: 'var(--vn-font-mono)', fontSize: 12,
    }}>
      <span style={{ color: 'var(--vn-text-muted)' }}>{label}</span>
      <span style={{ color: 'var(--vn-text-primary)', textAlign: 'right' }}>{value}</span>
    </div>
  );
}

// ───────────────────────── sections ─────────────────────────

const ACCENTS = ['green', 'yellow', 'blue', 'coral', 'violet'] as const;
const ACCENT_HEX: Record<string, string> = {
  green: '#22c55e', yellow: '#ffff00', blue: '#3b82f6', coral: '#f97316', violet: '#8b5cf6',
};

function AppearanceSection() {
  const { themeMode, setMode, setAccent, surface, setSurface, accentIcons, setAccentIcons, reset } = useTheme();
  const { name: accentName } = useAccent();
  const { mode: densityMode, setDensity } = useDensity();
  const { scale, setScale, formFactor } = useUiScale();

  return (
    <>
      <Group title="Appearance" description="Saved to the database, cached locally. Applies to Vudget only.">
        <Row label="Theme" control={
          <>
            {(['dark', 'light', 'system'] as const).map(m => (
              <Button key={m} size="xs" variant={themeMode === m ? 'primary' : 'ghost'} onClick={() => setMode(m)}>
                {m}
              </Button>
            ))}
          </>
        } />

        <Row label="Accent" help="Green reads as “surplus” everywhere else in the app." control={
          <>
            {ACCENTS.map(a => (
              <button key={a} onClick={() => setAccent(a)} title={a} aria-label={`Accent ${a}`}
                style={{
                  width: 22, height: 22, borderRadius: '50%', cursor: 'pointer', padding: 0,
                  background: ACCENT_HEX[a],
                  border: accentName === a ? '2px solid var(--vn-text-primary)' : '2px solid transparent',
                  outline: accentName === a ? 'none' : '1px solid var(--vn-border-default)',
                }} />
            ))}
          </>
        } />

        <Row label="Card surface" help="Outlined keeps the card background steady and separates with a hairline." control={
          <>
            {(['outlined', 'elevated', 'flat'] as const).map(v => (
              <Button key={v} size="xs" variant={surface === v ? 'primary' : 'ghost'} onClick={() => setSurface(v)}>
                {v}
              </Button>
            ))}
          </>
        } />

        <Row label={`Interface scale · ${formFactor === 'mobile' ? 'this phone' : 'desktop'}`}
          help={`Sizes the whole app the way browser zoom does — type, spacing and controls together. Saved per device class, so ${formFactor === 'mobile' ? 'changing it here leaves your desktop alone' : 'changing it here leaves your phone alone'}.`}
          control={
          <>
            {UI_SCALES.map(s => (
              <Button key={s} size="xs" variant={scale === s ? 'primary' : 'ghost'} onClick={() => setScale(s)}>
                {Math.round(s * 100)}%
              </Button>
            ))}
          </>
        } />

        <Row label="Density" help="How much air sits between things, independent of scale." control={
          <>
            {(['tight', 'comfortable'] as const).map(d => (
              <Button key={d} size="xs" variant={densityMode === d ? 'primary' : 'ghost'} onClick={() => setDensity(d)}>
                {d}
              </Button>
            ))}
          </>
        } />

        <Row label="Accent icons" help="Tint navigation icons with the accent colour."
          control={<Switch checked={accentIcons} onCheckedChange={setAccentIcons} />} />

        <Row label="Reset appearance" help="Back to dark · green · outlined · tight."
          control={<Button size="xs" variant="ghost" onClick={reset}>Reset</Button>} />
      </Group>
    </>
  );
}

function NavigationSection() {
  const settings = useStore(s => s.data?.settings);
  const [pinned, setPinned] = useState<string[]>(DEFAULT_PINNED);

  useEffect(() => {
    const raw = settings?.mobile_pinned_ids;
    if (!raw) return;
    try { const p = JSON.parse(raw); if (Array.isArray(p) && p.length) setPinned(p); } catch { /* defaults */ }
  }, [settings?.mobile_pinned_ids]);

  function toggle(id: string) {
    const next = pinned.includes(id)
      ? (pinned.length > 1 ? pinned.filter(p => p !== id) : pinned)
      : (pinned.length < 5 ? [...pinned, id] : pinned);
    setPinned(next);
    api.putSettings({ mobile_pinned_ids: JSON.stringify(next) }).catch(() => {});
  }

  const max = Math.min(5, NAV_ITEMS.length);

  return (
    <Group title="Mobile navigation"
      description={`Up to ${max} destinations sit in the bottom bar; the rest live behind “More”. You can also rearrange this from the phone.`}>
      {/*
        Pinned state was previously signalled by accent colour alone — no
        `aria-pressed`, no icon, nothing for anyone who can't pick the tint out
        of a hairline border. It now carries a check and reports its state to
        assistive tech, so "is this on?" is answerable without squinting.
      */}
      <Row stacked label="Pinned tabs" help={`${pinned.length} of ${max} pinned`} control={
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, maxWidth: 420 }}>
          {NAV_ITEMS.map(item => {
            const on = pinned.includes(item.id);
            const blocked = (!on && pinned.length >= max) || (on && pinned.length <= 1);
            const Icon = item.icon;
            return (
              <button key={item.id} onClick={() => toggle(item.id)} disabled={blocked}
                aria-pressed={on}
                title={blocked
                  ? (on ? 'At least one tab has to stay pinned' : `You already have ${max} pinned — unpin one first`)
                  : on ? `Unpin ${item.label}` : `Pin ${item.label}`}
                className={`vd-pin${on ? ' is-on' : ''}`}
                style={{ cursor: blocked ? 'not-allowed' : 'pointer', opacity: blocked ? 0.4 : 1 }}>
                {on ? <Check size={12} /> : <Icon size={12} />}
                {item.label}
              </button>
            );
          })}
        </div>
      } />
      {NAV_ITEMS.length <= max && (
        <Fact label="note" value={`All ${NAV_ITEMS.length} destinations fit, so nothing is hidden behind “More”.`} />
      )}
    </Group>
  );
}

const SOURCES = [
  ['Expenses',            '196df16107e281d2b059000bb3bce54d'],
  ['categories',          '196df16107e280748531000b06ac9f69'],
  ['frequencies',         '196df16107e28173a530000b01b40457'],
  ['Activity',            '196df16107e28197b0d6000bc56e7003'],
  ['Net Worth',           '25cdf16107e280fd86e6000bc0b669e3'],
  ['Income',              '25cdf16107e2809b9252000b1336c0e2'],
  ['Guilt Free Spending', '25cdf16107e28157946e000be42661ef'],
] as const;

function SyncSection() {
  const { data, syncing, sync, syncWarning, error, setSetting } = useStore();
  const [result, setResult] = useState<string | null>(null);
  const meta = data?.sync;
  const stale = (meta?.ageMinutes ?? Infinity) > 48 * 60;
  const pullToRefresh = data?.settings[PULL_TO_REFRESH_KEY] === '1';

  async function run() {
    setResult(null);
    await sync();
    const s = useStore.getState();
    setResult(s.error ? `Failed: ${s.error}` : 'Refreshed Notion and YNAB.');
  }

  return (
    <>
      <Group title="Notion sync"
        description="Notion is the source of truth. A refresh pulls it into the local database — one way, never back — and re-reconciles YNAB against what it pulled.">
        <Row
          label="Refresh everything"
          help={meta?.lastRunAt
            ? `Last run ${new Date(meta.lastRunAt).toLocaleString()}${stale ? ' — worth refreshing' : ''}`
            : 'No sync has run yet'}
          control={
            <Button size="sm" variant="primary" onClick={run} disabled={syncing}>
              <RefreshCw size={13} style={syncing ? { animation: 'vd-spin 1s linear infinite' } : undefined} />
              {syncing ? 'Refreshing…' : 'Refresh now'}
            </Button>
          }
        />
        <Row
          label="Pull to refresh"
          help="Drag down from the top of the Dashboard to sync. Phones only, and only on the Dashboard — anywhere with controls to reach for, the same drag would fire by accident."
          control={<Switch checked={pullToRefresh}
            onCheckedChange={on => setSetting(PULL_TO_REFRESH_KEY, on ? '1' : '0')} />}
        />
        <Row label="Scheduled" help="A cron also runs nightly at 06:00 ET — Notion and YNAB both."
          control={<span style={{ fontFamily: 'var(--vn-font-mono)', fontSize: 11, color: 'var(--vn-text-muted)' }}>0 10 * * *</span>} />
        <Row
          label="Funding vs YNAB"
          help="Comparing Notion against the budget provider — and the category and line-item mapping behind it — moved to its own page."
          control={
            // Link, not <a href> — a bare anchor would reload the whole SPA and
            // throw away the store the rest of Settings is reading from.
            <Link to="/funding" style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              fontSize: 12, color: 'var(--vn-text-secondary)',
            }}>
              <Scale size={13} />
              Open Funding
            </Link>
          }
        />
        {(result || error) && (
          <div style={{
            padding: '11px 15px', borderBottom: '1px solid var(--vn-border-default)',
            fontFamily: 'var(--vn-font-mono)', fontSize: 11.5,
            color: (result ?? error)!.startsWith('Failed') ? 'var(--vn-danger)' : 'var(--vn-success)',
            display: 'flex', alignItems: 'center', gap: 6,
          }}>
            {(result ?? error)!.startsWith('Failed') ? <AlertCircle size={13} /> : <CheckCircle2 size={13} />}
            {result ?? error}
          </div>
        )}
        {syncWarning && (
          <div style={{
            padding: '11px 15px', borderBottom: '1px solid var(--vn-border-default)',
            fontFamily: 'var(--vn-font-mono)', fontSize: 11.5,
            color: 'var(--vn-warning, #d97706)', display: 'flex', alignItems: 'center', gap: 6,
          }}>
            <AlertCircle size={13} />
            {syncWarning}
          </div>
        )}
      </Group>

      <NotionWebhookGroup />

      <Group title="Data sources" description="Seven Notion databases feed the mirror.">
        {SOURCES.map(([label, id]) => (
          <Row key={id} label={label} control={
            <a href={`https://www.notion.so/${id}`} target="_blank" rel="noreferrer"
              style={{ color: 'var(--vn-text-muted)', display: 'inline-flex' }} title="Open in Notion">
              <ExternalLink size={13} />
            </a>
          } />
        ))}
      </Group>

      <style>{`@keyframes vd-spin{to{transform:rotate(360deg)}}`}</style>
    </>
  );
}

/**
 * The push subscription, and the two-step handshake it needs to exist.
 *
 * Setting a Notion webhook up is not a button we can press: Notion posts a
 * one-time token to the endpoint, and that token has to be pasted back into
 * Notion's own UI. Without somewhere to read it, that token is only visible in
 * `wrangler tail` at the moment it arrives — so this panel exists mostly to
 * hand it back, and afterwards to answer "is it actually firing?".
 */
function NotionWebhookGroup() {
  const [s, setS] = useState<NotionWebhookStatus | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  const reload = () => api.notionWebhook().then(setS).catch(() => setS(null));
  useEffect(() => { reload(); }, []);

  async function copy(label: string, value: string) {
    await navigator.clipboard.writeText(value);
    setCopied(label);
    setTimeout(() => setCopied(null), 1500);
  }

  if (!s) return null;

  const copyBtn = (label: string, value: string) => (
    <Button size="sm" variant="secondary" onClick={() => copy(label, value)}>
      {copied === label ? <Check size={13} /> : <ExternalLink size={13} />}
      {copied === label ? 'Copied' : 'Copy'}
    </Button>
  );

  return (
    <Group title="Notion webhook"
      description="Notion pushes every edit here, so the mirror updates on its own. Edits are coalesced for a few seconds first — one burst of changes costs one sync, not one per cell.">
      <Row label="Endpoint" help="Paste this into Notion → Integrations → Webhooks."
        control={copyBtn('endpoint', s.endpoint)} />

      {!s.configured && (
        <Row label="Not verified yet" control={null}
          help="Add the endpoint in Notion and press Verify. Notion posts a token here, which appears in this panel once it lands — press Check to see it." />
      )}

      {s.configured && s.verificationToken && (
        <Row label="Verification token"
          help="Paste this back into Notion to finish verification. It only proves Notion can reach this endpoint — it grants no access to the workspace."
          control={copyBtn('token', s.verificationToken)} />
      )}

      {s.verifiedAt && (
        <Row label="Verified" control={null} help={new Date(s.verifiedAt).toLocaleString()} />
      )}
      <Row label="Last event" control={null}
        help={s.lastEventAt
          ? `${new Date(s.lastEventAt).toLocaleString()} · ${s.lastEventType ?? 'unknown'}`
          : 'No event received yet'} />
      <Row label="Last webhook sync"
        help={s.lastWebhookSyncAt
          ? new Date(s.lastWebhookSyncAt).toLocaleString()
          : 'None yet'}
        control={
          <Button size="sm" variant="secondary" onClick={reload}>
            <RefreshCw size={13} />
            Check
          </Button>
        } />
      {s.pending && (
        <Row label="Pending" control={null} help="Edits have arrived and a sync is queued or in flight." />
      )}
      {s.lastError && (
        <div style={{
          padding: '11px 15px', fontFamily: 'var(--vn-font-mono)', fontSize: 11.5,
          color: 'var(--vn-danger)', display: 'flex', alignItems: 'center', gap: 6,
        }}>
          <AlertCircle size={13} />
          {s.lastError}
        </div>
      )}
    </Group>
  );
}

function DataSection() {
  const data = useStore(s => s.data);
  return (
    <>
      <Group title="Local mirror" description="What the last sync pulled in.">
        <Fact label="Expenses" value={data?.expenses.length ?? 0} />
        <Fact label="Categories" value={data?.categories.length ?? 0} />
        <Fact label="Recurrences" value={data?.recurrences.length ?? 0} />
        <Fact label="Net-worth snapshots" value={data?.netWorth.length ?? 0} />
        <Fact label="Scenarios" value={data?.scenarios.length ?? 0} />
        <Fact label="Net income" value={`${fmtMoney(data?.income.netMonthly ?? 0)}/mo`} />
        <Fact label="Guilt-free budget" value={`${fmtMoney(data?.gfsBudget ?? 0)}/mo`} />
      </Group>

      <Group title="How the maths works"
        description="Notion's API returns formula properties as null, so monthly cost is recomputed locally.">
        <Fact label="Days per month" value="30.4375" />
        <Fact label="per_month" value="30.4375 ÷ (freq × multDays)" />
        <Fact label="monthly" value="amt × tax × units ÷ split × per_month" />
      </Group>
    </>
  );
}

function AccountSection() {
  const data = useStore(s => s.data);
  const [email, setEmail] = useState<string | null>(null);
  useEffect(() => { api.me().then(r => setEmail(r.email)).catch(() => {}); }, []);

  return (
    <>
      <Group title="Account"
        description="Cloudflare Access gates the hostname at the edge; the API verifies the token again on every request.">
        <Fact label="Signed in as" value={email ?? '…'} />
        <Fact label="Auth" value="Cloudflare Access" />
        <Fact label="Environment" value={data?.settings.environment ?? 'development'} />
        <Fact label="Write-back to Notion" value="disabled by design" />
      </Group>

      {/*
        Stamped into the bundle at build time, not read back from the API, so it
        describes the JavaScript this browser is running. That distinction is
        the whole point on an installed home-screen app, where a cached shell
        can sit a deploy or two behind the server — exactly the situation where
        "is my change live?" gets asked. A trailing `+` on the commit means it
        was built from a working tree with uncommitted changes.
      */}
      <Group title="Build" description="Which version of Vudget this device is running.">
        <Fact label="Version" value={BUILD.version} />
        <Fact label="Commit" value={BUILD.commit} />
        <Fact label="Built" value={buildDate()} />
      </Group>
    </>
  );
}
